import { SkeletonBlock } from '@/components/common/Skeleton';
import { FindingOverlay } from '@/components/review/FindingOverlay';
import { type PaintedCanvas, useSubstitutePreview } from '@/hooks/useSubstitutePreview';
import type { CategorySummary, ExportMode, FindingModel, PageModel } from '@/types/documents.types';
import { fitScale } from '@/utils/geometry';
import { resolveSelection } from '@/utils/selection';
import { replaceFindingsInRange } from '@/utils/substituteLines';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import type { ClipboardEvent, FC, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The prototype's paper width at 100 %, and the room it keeps to the column edges. */
const PAGE_BASE_WIDTH = 640;
const PAGE_GUTTER = 40;

interface PdfPageViewProps {
  pdf: PDFDocumentProxy | null;
  page: PageModel | null;
  pageNumber: number;
  /** User zoom on top of the fit-to-width scale. */
  zoom: number;
  mode: ExportMode;
  findings: FindingModel[];
  /** The category a finding is drawn in (its display category in the session snapshot). */
  categoryOf: (finding: FindingModel) => CategorySummary | undefined;
  /** Decides per finding whether the export will touch it. */
  isInExport: (finding: FindingModel) => boolean;
  selectedFindingId: string | null;
  onSelectFinding: (findingId: string) => void;
  /** The text layer element, so the route can read selections out of it. */
  textLayerRef: RefObject<HTMLDivElement | null>;
  /** Raised after a selection gesture inside the page. */
  onTextSelection: () => void;
}

/**
 * One rendered page: the canvas pdf.js paints, its text layer, and the finding overlays.
 *
 * Three stacked layers in one positioned box. The canvas is the picture, the text layer is
 * invisible text placed exactly over it so the browser's own selection works, and the overlay
 * layer sits on top with `pointer-events-none` — only the finding boxes inside it take clicks, so
 * selecting text between them still reaches the text layer underneath.
 *
 * The canvas is sized in device pixels and scaled back down with CSS, which is what keeps glyph
 * edges sharp on a high-DPI screen; the overlays are positioned in CSS pixels, using the same
 * `factor` the finding rectangles are scaled by.
 *
 * "Exactly over" takes three things, because pdf.js leaves most of a span's geometry to CSS: the
 * `.textLayer` rules ported into `styles/index.css`, the `--total-scale-factor` this component
 * sets on the layer, and a layer box of the same pixel size as the canvas box — the span offsets
 * are percentages of that box, so a difference of one pixel already drifts the text.
 */
export const PdfPageView: FC<PdfPageViewProps> = ({
  pdf,
  page,
  pageNumber,
  zoom,
  mode,
  findings,
  categoryOf,
  isInExport,
  selectedFindingId,
  onSelectFinding,
  textLayerRef,
  onTextSelection,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** The render in flight. pdf.js refuses a second render onto a canvas that is still busy. */
  const renderTaskRef = useRef<RenderTask | null>(null);
  /** Identifies the latest render run, so a superseded one cannot clear the busy flag. */
  const renderRunRef = useRef(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isRendering, setIsRendering] = useState(false);
  /** The scale of the last paint that reached the canvas; the anonymized preview reads its colours. */
  const [painted, setPainted] = useState<PaintedCanvas>({ factor: 0, version: 0 });

  // The page is rendered at whatever width the column currently has, so the layout decides the
  // scale rather than a fixed zoom step.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    setContainerWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // At 100 % the page is the prototype's 640 px sheet, or the column less 40 px when that is
  // narrower; zoom scales from there.
  const baseWidth = Math.min(PAGE_BASE_WIDTH, containerWidth - PAGE_GUTTER);
  // Rounded to whole percent: a ResizeObserver reports sub-pixel width changes, and every one of
  // them would otherwise restart the render of an already correct page.
  const baseScale = page ? fitScale(baseWidth, page.width) : 0;
  const factor = Math.round(baseScale * zoom * 100) / 100;

  useEffect(() => {
    const canvas = canvasRef.current;
    const textLayer = textLayerRef.current;
    if (!pdf || !page || !canvas || factor <= 0) return;

    let cancelled = false;
    const runId = ++renderRunRef.current;

    const render = async () => {
      setIsRendering(true);
      try {
        // Stop whatever is still painting before touching the canvas: pdf.js throws when two
        // renders share one canvas, and a cleared-but-never-repainted canvas is what the user
        // would be left with.
        const running = renderTaskRef.current;
        if (running) {
          running.cancel();
          await running.promise.catch(() => undefined);
          renderTaskRef.current = null;
        }
        if (cancelled) return;

        const pdfPage = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = pdfPage.getViewport({ scale: factor });
        const ratio = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        // jsdom (and a browser that ran out of GPU memory) hands back no context; the overlays
        // and the text layer are still worth rendering, so this is not an error.
        const context = canvas.getContext('2d');
        if (context) {
          context.setTransform(ratio, 0, 0, ratio, 0, 0);
          const task = pdfPage.render({ canvas, canvasContext: context, viewport });
          renderTaskRef.current = task;
          await task.promise;
          renderTaskRef.current = null;
          if (!cancelled) setPainted((previous) => ({ factor, version: previous.version + 1 }));
        }
        if (cancelled || !textLayer) return;

        textLayer.replaceChildren();
        // pdf.js gives each span a `left`/`top` in percent of this box and a `--font-height` in
        // unscaled PDF pixels; the stylesheet turns the latter into a font size by multiplying it
        // with `--total-scale-factor`. Nothing sets that variable outside the pdf.js viewer, so the
        // layer carries it here, at the very scale the canvas was painted at.
        textLayer.style.setProperty('--total-scale-factor', `${factor}`);
        const { pdfjs } = await import('@/utils/pdf');
        const layer = new pdfjs.TextLayer({
          textContentSource: pdfPage.streamTextContent(),
          container: textLayer,
          viewport,
        });
        // The constructor above has just overwritten width and height with its own
        // `round(down, var(--total-scale-factor) * <page size>px, var(--scale-round-x))`
        // expressions, which need a variable only the viewer's page stylesheet defines. These two
        // lines put the box back in pixels, identical to the canvas box, which is what the span
        // percentages are measured against.
        textLayer.style.width = `${Math.floor(viewport.width)}px`;
        textLayer.style.height = `${Math.floor(viewport.height)}px`;
        await layer.render();
      } catch (error) {
        // A cancelled render rejects; that is the normal path when the user pages or resizes.
        if (!cancelled) console.warn('Page render failed', error);
      } finally {
        // Only the newest run owns the flag: a run cancelled by a resize must not clear it while
        // its successor is still painting, and must clear it when it is the last one standing.
        if (renderRunRef.current === runId) setIsRendering(false);
      }
    };

    render();
    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [pdf, page, pageNumber, factor, textLayerRef]);

  const previews = useSubstitutePreview({
    enabled: mode === 'anonymized',
    pdf,
    pageNumber,
    findings,
    canvasRef,
    factor,
    painted,
  });

  // The text layer always holds the original text; outside the original view, copying it would
  // leak exactly what the export hides, so the clipboard gets the range as the export shows it.
  // An ambiguous selection takes its first occurrence; one that cannot be mapped copies nothing.
  const handleCopy = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!page || mode === 'original') return;
    event.preventDefault();
    const selection = window.getSelection();
    const anchor = selection?.anchorNode;
    const context = (anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : (anchor as Element | null))
      ?.textContent;
    const resolution = resolveSelection(page.text, selection?.toString() ?? '', context ?? '');
    const candidate =
      resolution.kind === 'none'
        ? null
        : resolution.kind === 'unique'
          ? resolution.candidate
          : resolution.candidates[0];
    const text = candidate
      ? replaceFindingsInRange(page.text, candidate.start, candidate.end, findings.filter(isInExport), mode)
      : '';
    event.clipboardData.setData('text/plain', text);
  };

  const width = page && factor > 0 ? Math.floor(page.width * factor) : 0;
  const height = page && factor > 0 ? Math.floor(page.height * factor) : 0;

  return (
    <div ref={containerRef} className="w-full">
      {page && factor > 0 ? (
        <div
          className="relative mx-auto bg-white shadow-[0_2px_14px_rgba(10,45,80,.11)]"
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <canvas ref={canvasRef} className="block" aria-label={`${t('common.page')} ${pageNumber}`} />
          {/* `textLayer` is the pdf.js contract, not a Tailwind class: the rules ported into
              `styles/index.css` are what place this box over the canvas and make its spans
              absolutely positioned and transparent. Only `select-text` is this app's own. */}
          <div
            ref={textLayerRef}
            onMouseUp={onTextSelection}
            onKeyUp={onTextSelection}
            onCopy={handleCopy}
            className="textLayer select-text"
          />
          <div
            className="pointer-events-none absolute left-0 top-0"
            style={{ width: `${width}px`, height: `${height}px` }}
          >
            {findings.map((finding) => (
              <FindingOverlay
                key={finding.id}
                finding={finding}
                color={categoryOf(finding)?.color}
                factor={factor}
                mode={mode}
                inExport={isInExport(finding)}
                selected={finding.id === selectedFindingId}
                onSelect={onSelectFinding}
                preview={previews.get(finding.id)}
              />
            ))}
          </div>
          {isRendering ? (
            <span className="absolute right-2 top-2 bg-white/90 px-2 py-1 text-[11px] text-subtle">
              {t('review.page.loading')}
            </span>
          ) : null}
        </div>
      ) : (
        <SkeletonBlock className="mx-auto aspect-[1/1.414] w-full max-w-[640px]" />
      )}
    </div>
  );
};
